'use client';

// React component for Solflare navigation, based on navigation.html
// All class names and IDs are preserved for style compatibility
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './navigation.scss';
import Image from 'next/image';
import NavigationTicker, { TickerSlide } from './NavigationTicker';
import NavigationLink from './NavigationLink';
import SubmenuItem from './SubmenuItem';
import DropdownTrigger from './DropdownTrigger';
// import { ArrowDownIcon, NotificationDot } from './NavigationIcons';

// Type definitions for menu items
export interface MenuItem {
	label: string;
	href: string;
	tag?: string;
}

export interface MenuSection {
	label: string;
	items: MenuItem[];
}

// Menu data structure for better maintainability
const MENU_DATA: Record<string, MenuSection> = {
	product: {
		label: 'Product',
		items: [
			{ label: 'Overview', href: 'https://www.solflare.com/product/' },
			{ label: 'Essentials', href: 'https://www.solflare.com/essentials/' },
			{ label: 'Security', href: 'https://www.solflare.com/security/' },
			{ label: 'Trading', href: 'https://www.solflare.com/trading/' },
			{ label: 'Staking', href: 'https://www.solflare.com/stake/' },
			{ label: 'Cards', href: 'https://www.solflare.com/crypto-card/', tag: 'New' },
		],
	},
	resources: {
		label: 'Resources',
		items: [
			{ label: 'Overview', href: 'https://www.solflare.com/resources/' },
			{ label: 'Crypto 101', href: 'https://www.solflare.com/crypto-101/' },
			{ label: 'App Guides', href: 'https://www.solflare.com/guides/' },
			{ label: 'FAQs', href: 'https://www.solflare.com/faq/' },
			{ label: 'Glossary', href: 'https://www.solflare.com/glossary/' },
			// { label: 'Company Blog', href: 'https://www.solflare.com/company-blog/' },
		],
	},
	exploreSolana: {
		label: 'Explore Solana',
		items: [
			{ label: 'Overview', href: 'https://www.solflare.com/explore-solana/' },
			{ label: 'Ecosystem', href: 'https://www.solflare.com/ecosystem/' },
			{ label: 'News', href: 'https://www.solflare.com/news/' },
			{ label: 'Events', href: 'https://www.solflare.com/events/' },
		],
	},
};

// Menu structure with proper order (dropdowns and standalone links mixed)
const MENU_STRUCTURE = [
	{ type: 'dropdown', key: 'product', overviewHref: 'https://www.solflare.com/product/' },
	{ type: 'link', href: '/prices/', label: 'Token Prices' },
	{ type: 'dropdown', key: 'resources', overviewHref: 'https://www.solflare.com/resources/' },
	{ type: 'dropdown', key: 'exploreSolana', overviewHref: 'https://www.solflare.com/explore-solana/' },
	{ type: 'link', href: 'https://www.solflare.com/about/', label: 'About' },
	{ type: 'link', href: 'https://www.solflare.com/careers/', label: 'Careers' },
] as const;

const TICKER_SLIDES: TickerSlide[] = [
	{
		date: '29th September 2025',
		title: 'Solana APEX - Singapore',
		link: 'https://lu.ma/apex-singapore',
	},
	{
		date: '6th October 2025',
		title: 'Solana APEX - Mumbai',
		link: 'https://lu.ma/apex-mumbai',
	},
	{
		date: '11th December 2025',
		title: 'Solana Breakpoint - Abu Dhabi',
		link: 'https://solana.com/breakpoint',
	},
];

const MOBILE_MENU_CLASS = 'is-mobile-navigation-open-globally';

// Custom hook for navigation state management
const useNavigationState = () => {
	const [mobileOpen, setMobileOpen] = useState(false);
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

	const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);
	const toggleExpanded = useCallback((idx: number) => 
		setExpandedIndex(prev => prev === idx ? null : idx), []);
	const setDropdown = useCallback((dropdown: string | null) => 
		setActiveDropdown(dropdown), []);

	return {
		mobileOpen,
		expandedIndex,
		activeDropdown,
		toggleMobile,
		toggleExpanded,
		setDropdown,
	};
};

// Optimized dropdown component
const NavigationDropdown: React.FC<{
	menuKey: string;
	menuData: MenuSection;
	isActive: boolean;
	onMouseEnter: () => void;
	onMouseLeave?: () => void;
}> = React.memo(({ menuKey, menuData, isActive, onMouseEnter, onMouseLeave }) => (
	<div
		className={`c-navigation__submenu-wrapper js-navigation-submenu-wrapper${isActive ? ' is-open' : ''}`}
		style={{
			height: isActive ? 240 : 0,
			transition: 'height 0.3s',
		}}
	>
		<div 
			className="c-navigation__submenu-background"
			onMouseEnter={onMouseEnter}
			{...(onMouseLeave && { onMouseLeave })}
		>
			<ul className="c-navigation__submenu js-navigation-submenu">
				{menuData.items.map((item, index) => (
					<SubmenuItem
						key={`${menuKey}-${index}`}
						item={item}
						keyPrefix={menuKey}
						index={index}
					/>
				))}
			</ul>
		</div>
	</div>
));

NavigationDropdown.displayName = 'NavigationDropdown';

// Optimized mobile menu item component
const MobileMenuItem: React.FC<{
	menuKey: string;
	menuData: MenuSection;
	index: number;
	isExpanded: boolean;
	onToggle: (index: number) => void;
}> = React.memo(({ menuKey, menuData, index, isExpanded, onToggle }) => (
	<li className={`c-mobile-navigation__menu-item js-expander${isExpanded ? ' is-expanded' : ''}`}>
		<DropdownTrigger
			label={menuData.label}
			isMobile
			onClick={(e) => {
				e.preventDefault();
				onToggle(index);
			}}
		/>
		<ul
			className="c-mobile-navigation__submenu js-expander-content"
			style={{
				height: isExpanded ? 'auto' : 0,
			}}
		>
			{menuData.items.map((item, itemIndex) => (
				<SubmenuItem
					key={`${menuKey}-${itemIndex}`}
					item={item}
					keyPrefix={menuKey}
					index={itemIndex}
					isMobile
				/>
			))}
		</ul>
	</li>
));

MobileMenuItem.displayName = 'MobileMenuItem';

// Dedicated Navigation Links component for better organization
const NavigationLinks: React.FC<{
	activeDropdown: string | null;
	onDropdownEnter: (key: string) => void;
	onDropdownLeave: () => void;
}> = React.memo(({ activeDropdown, onDropdownEnter, onDropdownLeave }) => (
	<>
		{MENU_STRUCTURE.map((item, index) => {
			if (item.type === 'dropdown') {
				const menuData = MENU_DATA[item.key];
				return (
					<li
						key={item.key}
						className="c-navigation__menu-item"
						onMouseEnter={() => onDropdownEnter(item.key)}
					>
						<DropdownTrigger
							label={menuData.label}
							isActive={activeDropdown === item.key}
							href={item.overviewHref}
						/>
						<NavigationDropdown
							menuKey={item.key}
							menuData={menuData}
							isActive={activeDropdown === item.key}
							onMouseEnter={() => onDropdownEnter(item.key)}
						/>
					</li>
				);
			} else {
				return (
					<NavigationLink 
						key={item.href} 
						href={item.href}
						onMouseEnter={onDropdownLeave}
					>
						{item.label}
					</NavigationLink>
				);
			}
		})}
	</>
));

NavigationLinks.displayName = 'NavigationLinks';



const Navigation: React.FC = () => {
	const {
		mobileOpen,
		expandedIndex,
		activeDropdown,
		toggleMobile,
		toggleExpanded,
		setDropdown,
	} = useNavigationState();

	const navWrapperRef = useRef<HTMLDivElement>(null);

	// Toggle body class for mobile nav
	useEffect(() => {
		if (mobileOpen) {
			document.body.classList.add(MOBILE_MENU_CLASS);
		} else {
			document.body.classList.remove(MOBILE_MENU_CLASS);
		}
		return () => {
			document.body.classList.remove(MOBILE_MENU_CLASS);
		};
	}, [mobileOpen]);

	// Optimized scroll handler with throttling
	useEffect(() => {
		let lastScroll = window.scrollY;
		let ticking = false;

		const onScroll = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					const nav = navWrapperRef.current;
					if (!nav) return;
					
					const currentScroll = window.scrollY;
					if (currentScroll > lastScroll && currentScroll > 200) {
						nav.classList.add('slide-up');
					} else {
						nav.classList.remove('slide-up');
					}
					lastScroll = currentScroll;
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	// Desktop dropdown handlers
	const handleDropdownEnter = useCallback((dropdownKey: string) => {
		setDropdown(dropdownKey);
	}, [setDropdown]);

	const handleDropdownLeave = useCallback(() => {
		setDropdown(null);
	}, [setDropdown]);

	// Mobile menu component
	const mobileMenu = (
		<nav className="o-mobile-navigation">
			<div className="o-container">
				<div className={`c-mobile-navigation js-mobile-navigation${mobileOpen ? ' is-open' : ''}`}>
					<ul className="c-mobile-navigation__menu">
						{MENU_STRUCTURE.map((item, index) => {
							if (item.type === 'dropdown') {
								const menuData = MENU_DATA[item.key];
								return (
									<MobileMenuItem
										key={item.key}
										menuKey={item.key}
										menuData={menuData}
										index={index}
										isExpanded={expandedIndex === index}
										onToggle={toggleExpanded}
									/>
								);
							} else {
								return (
									<NavigationLink key={item.href} href={item.href} isMobile>
										{item.label}
									</NavigationLink>
								);
							}
						})}
					</ul>
					<div className="c-mobile-navigation__ticker js-mobile-navigation-ticker-wrapper">
						<NavigationTicker 
							slides={TICKER_SLIDES} 
							isVisible={mobileOpen}
							className="js-mobile-navigation-ticker"
							isMobile={true}
						/>
					</div>
				</div>
			</div>
		</nav>
	);

	return (
		<>
			<nav
				className="o-navigation js-navigation js-navigation-wrapper"
				ref={navWrapperRef}
				onMouseLeave={handleDropdownLeave}
			>
				<div className="o-container">
					<div className="c-navigation">
						<a
							href="https://www.solflare.com"
							className="c-navigation__logo"
						>
							<Image
								fetchPriority="high"
								src="/tkn_lnd_pg_assets/logo.svg"
								alt="Solflare logo"
								width={40}
								height={40}
							/>
						</a>
						<ul className="c-navigation__menu js-navigation-menu">
							<NavigationLinks
								activeDropdown={activeDropdown}
								onDropdownEnter={handleDropdownEnter}
								onDropdownLeave={handleDropdownLeave}
							/>
						</ul>
						<div className="c-navigation__actions">
							<a
								href="https://www.solflare.com/download/"
								className="c-button c-button--yellow"
								aria-label="Download now"
							>
								<div>
									<span>Download now</span>
									<span aria-hidden="true">Download now</span>
								</div>
							</a>
						</div>
						<div className="c-navigation__actions c-navigation__actions--mobile">
							<a
								href="https://www.solflare.com/download/"
								className="c-button c-button--yellow c-button--mobile"
								aria-label="Download now"
							>
								<div>
									<span>Download now</span>
									<span aria-hidden="true">Download now</span>
								</div>
							</a>
							<button
								className={`c-hamburger js-mobile-navigation-trigger${mobileOpen ? ' is-open' : ''}`}
								aria-label="Open mobile menu"
								aria-expanded={mobileOpen}
								aria-controls="c-mobile-navigation"
								onClick={toggleMobile}
							>
								<span className="c-hamburger__inner">
									<i className="c-hamburger__line" aria-hidden="true"></i>
									<i className="c-hamburger__line" aria-hidden="true"></i>
									<span className="u-screen-reader-text">Menu</span>
								</span>
							</button>
						</div>
						<NavigationTicker 
							slides={TICKER_SLIDES} 
							isVisible={!!activeDropdown}
							onMouseLeave={handleDropdownLeave}
						/>
					</div>
				</div>
			</nav>
			{mobileMenu}
		</>
	);
};

export default Navigation;
